// Per spec/blocks.md §11. Community section explaining chapter WhatsApp
// groups, with a per-state link grid sourced from the per-Email-editable
// `inputs.groupsByState` (P-037.23 / D-131 — Option C: URLs AND labels
// editable per Email so chapters can add/rename groups over time without
// a code change).
//
// **P-037.23 / D-131 rework.** Banks's audit: `options.states` (pick
// which states render) was vestigial — the chapter always wants all 7.
// What actually changes between Emails is the URLs (and occasionally
// group labels as new sub-groups appear). New shape:
//
// - `optionsSchema = z.object({})` — no Options at all.
// - `inputs.chapterWideGroups: WhatsAppGroup[]` (max 4) — chapter-wide
//   groups (S7 Member + S7 Spouses/Partners by default).
// - `inputs.groupsByState: Record<StateId, WhatsAppGroup[]>` (max 5 per
//   state) — per-Email-editable groups for each state. Default seeds
//   from the state registry's `whatsappGroups`. Shape auto-tracks
//   STATE_IDS via `Object.fromEntries(...)` so adding a state to the
//   registry propagates here without a code change at this site.
//
// **P-037.25 / D-133 — Southern 7 grid cell.** Banks drops the
// previously-centered "S7 Member · S7 Spouses/Partners" middot line
// (rendered between the description and the per-state grid post-D-131)
// and promotes `chapterWideGroups` into the grid itself as a synthetic
// "Southern 7" cell prepended before Alabama. The grid expands from
// 7 → 8 entries: `[S7, AL, GA] / [MS, NC, SC] / [TN, VA, _]`. The
// synthetic state shape `{ name: 'Southern 7', flagImg: '/ypo-flag.png' }`
// satisfies WhatsAppRow's `Pick<State, 'name' | 'flagImg'>` prop (D-131).
//
// **P-037.29 D-137 — required `description` richText field DROPPED**
// per Banks's "only `text` + `time-location-cost` carry richText"
// rule. Banks-confirmed at planning: grid-flush-to-top is fine; the
// prev Block above provides the visual gap via the system-level
// spacer (P-037.29 / D-137 item 2). Schema v10 → v11 migration is a
// Zod-strip pass-through. Staff composes a separate `text` Block
// above chapter-whatsapp when they want intro copy.
//
// Render shape (in a 30 px L/R outer cell — matches events-list /
// event-card-group):
//   8-cell 3-col grid (Southern 7 first, then 7 states in registry
//   order). WhatsAppRow consumes `inputs.chapterWideGroups` for the
//   Southern 7 cell and `inputs.groupsByState[id]` for each state.
//
// Auto-hide: none. All 8 grid cells always render.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { whatsAppRow } from '../components/whatsapp-row'
import { gridLayout } from '../components/helpers'
import { states, STATE_IDS, type StateId, type WhatsAppGroup } from '../registry/states'

const whatsappGroupSchema = z.object({
  label: z.string().min(1),
  url: z.string().url(),
})

// Per-Email-editable per-state group lists. Schema shape auto-tracks
// STATE_IDS so adding a state to the registry propagates here without a
// code change. The `as unknown as Record<StateId, ...>` two-step cast is
// needed because TypeScript can't statically prove that
// `Object.fromEntries(STATE_IDS.map(...))` produces all 7 keys —
// the underlying types DO overlap (both keys-of are StateId), but
// strict-mode object-shape checks reject the single-step cast.
const stateGroupsArraySchema = z.array(whatsappGroupSchema).max(5)
const groupsByStateShape = Object.fromEntries(
  STATE_IDS.map((id) => [id, stateGroupsArraySchema]),
) as unknown as Record<StateId, typeof stateGroupsArraySchema>
const groupsByStateSchema = z.object(groupsByStateShape)

// Chapter-wide groups (S7 Member + S7 Spouses/Partners). Default seed
// per Banks's confirmed URLs. Chapters can add up to 4 entries over
// time as new chapter-wide groups appear.
const DEFAULT_CHAPTER_WIDE_GROUPS: ReadonlyArray<WhatsAppGroup> = [
  { label: 'S7 Member', url: 'https://chat.whatsapp.com/Lxk3AuZZ3Yt9AB3T0oOArs' },
  {
    label: 'S7 Spouses/Partners',
    url: 'https://chat.whatsapp.com/CIxf37cxf017f2v5S53jJo',
  },
]

// Per-state defaults derived from the registry. Mirrors the registry's
// shape exactly; the registry update at P-037.23 (Banks-confirmed URLs)
// flows through here without further edits.
function makeDefaultGroupsByState(): Record<StateId, WhatsAppGroup[]> {
  return Object.fromEntries(
    STATE_IDS.map((id) => [id, states[id].whatsappGroups.map((g) => ({ ...g }))]),
  ) as unknown as Record<StateId, WhatsAppGroup[]>
}

const optionsSchema = z.object({})

const inputsSchema = z.object({
  chapterWideGroups: z
    .array(whatsappGroupSchema)
    .max(4)
    .default([...DEFAULT_CHAPTER_WIDE_GROUPS]),
  groupsByState: groupsByStateSchema.default(makeDefaultGroupsByState()),
})

// P-037.25 D-133: synthetic "Southern 7" cell prepended to the per-state
// grid. The `flagImg` is the new chapter wordmark asset
// (`public/ypo-flag.png`). Satisfies WhatsAppRow's narrowed
// `Pick<State, 'name' | 'flagImg'>` prop (D-131 §6).
const SOUTHERN_7_SYNTHETIC_STATE = {
  name: 'Southern 7',
  flagImg: '/ypo-flag.png',
} as const

export const chapterWhatsapp = defineBlock({
  id: 'chapter-whatsapp',
  label: 'Chapter WhatsApp',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: {
    chapterWideGroups: DEFAULT_CHAPTER_WIDE_GROUPS.map((g) => ({ ...g })),
    groupsByState: makeDefaultGroupsByState(),
  },
  render: ({ inputs }, ctx) => {
    // P-037.18 D-118: thread ctx.linkColor through whatsAppRow so the
    // per-group anchor color matches the template-level link color
    // derived from the Header style.
    // P-037.25 D-133: 8-cell grid — synthetic Southern 7 entry first,
    // then 7 states in registry order. WhatsAppRow consumes the
    // appropriate per-cell groups list.
    type GridEntry = {
      readonly state: { readonly name: string; readonly flagImg: string }
      readonly groups: ReadonlyArray<WhatsAppGroup>
    }
    const gridEntries: ReadonlyArray<GridEntry> = [
      { state: SOUTHERN_7_SYNTHETIC_STATE, groups: inputs.chapterWideGroups },
      ...STATE_IDS.map((id: StateId) => ({
        state: states[id],
        groups: inputs.groupsByState[id],
      })),
    ]
    const grid = gridLayout(gridEntries, 3, (entry) =>
      whatsAppRow({
        state: entry.state,
        groups: entry.groups,
        linkColor: ctx.linkColor,
      }),
    )
    // P-037.15: 30 px L/R padding on the content. The baked
    // SectionHeader was dropped at P-037.20 / D-126 + the required
    // `description` richText row was dropped at P-037.29 / D-137 —
    // users compose `section-heading` + `text` Blocks above this one
    // when they want a heading and/or description copy.
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${grid}</td></tr></table>`
  },
})
